/**
 * ConsentRenewalModal — Kenya DPA compliance.
 *
 * When the platform bumps the Terms / Privacy / Cookie / Disclaimer policy
 * version, every already-registered user must re-accept before continuing
 * to use the service. This modal:
 *
 *   1. Polls `/api/legal/my-consent` after auth is confirmed.
 *   2. If the server flags `needs_renewal`, opens as a non-dismissable modal.
 *   3. Calls `POST /api/legal/accept` on confirmation, then refreshes the
 *      auth context so the flag flips off.
 *
 * The modal is deliberately closed-loop: no external redirects, no email.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

// Human-friendly labels for the policy keys the backend returns.
const POLICY_LABELS = {
    terms:      { label: "Terms of Service",   href: "/legal/terms" },
    privacy:    { label: "Privacy Policy",     href: "/legal/privacy" },
    cookies:    { label: "Cookie Policy",      href: "/legal/cookies" },
    disclaimer: { label: "Risk Disclaimer",    href: "/legal/disclaimer" },
};

// `active`: only run the my-consent check while this modal owns the
// sequencing slot (see App.js `ConsentCoordinator`) — TelemetryConsentModal
// waits for `onSettled` before it takes its turn.
// `onSettled`: fired once this modal has nothing left to show (either no
// renewal was needed, or the user just accepted) so the next modal in the
// sequence can open.
export default function ConsentRenewalModal({ active = true, onSettled }) {
    const { user } = useAuth();
    const [state, setState] = useState({ open: false, current: {}, accepted: {} });
    const [confirmed, setConfirmed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Mount-once guard — a transient `user` object change (e.g. some other
    // part of the app calling `setUser` with a new reference) must never
    // re-trigger the my-consent check and re-open this modal.
    const checkedRef = useRef(false);

    // Only check consent for authenticated users. Skip for guests + super-admin
    // (admin accounts predate the versioning system and are covered by
    // separate onboarding).
    useEffect(() => {
        if (!active || checkedRef.current) return;
        if (!user || user === false) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/legal/my-consent");
                if (cancelled) return;
                // Mark "checked" only once a non-cancelled response actually
                // lands — NOT before starting the fetch. React StrictMode
                // double-invokes effects in dev (mount -> cleanup -> mount
                // again); marking the ref eagerly meant the *first*
                // invocation's in-flight request got cancelled by its own
                // cleanup, while the *second* invocation was blocked by the
                // ref already being true — net result, no request's
                // result was ever applied and the modal never opened.
                checkedRef.current = true;
                if (data.needs_renewal) {
                    setState({ open: true, current: data.current || {}, accepted: data.accepted || {} });
                } else {
                    onSettled?.();
                }
            } catch {
                // Never block the app if the endpoint is transiently down —
                // the next login will retry. Still hand off to the next
                // modal in the sequence.
                if (cancelled) return;
                checkedRef.current = true;
                onSettled?.();
            }
        })();
        return () => { cancelled = true; };
    }, [active, user, onSettled]);

    async function handleAccept() {
        if (!confirmed) return;
        setSubmitting(true);
        try {
            await api.post("/legal/accept", { policies: Object.keys(state.current) });
            // Optimistic close — no full `refresh()` round-trip. Nothing on
            // the `user` object itself needs updating for this modal (its
            // own `needs_renewal` state lives in local `state`, not in the
            // auth context), so re-fetching `/auth/me` here only re-ran the
            // whole hydration cascade for no benefit.
            toast.success("Thanks — your consent is on file.");
            setState((s) => ({ ...s, open: false }));
            onSettled?.();
        } catch (err) {
            toast.error("Could not record your acceptance. Please retry.");
        } finally {
            setSubmitting(false);
        }
    }

    if (!state.open) return null;

    // Which policies have changed vs. the user's previously-accepted versions?
    const changedKeys = Object.keys(state.current).filter(
        (k) => state.accepted?.[k] !== state.current[k]?.version,
    );
    const isFirstTime = Object.keys(state.accepted || {}).length === 0;

    return (
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            style={{ pointerEvents: "auto" }}
            data-testid="consent-renewal-modal"
        >
            <div className="bg-card border border-primary/40 rounded-2xl max-w-lg w-full shadow-2xl text-card-foreground">
                <div className="px-6 pt-6 pb-4 border-b border-border">
                    <p className="text-xs uppercase tracking-widest text-primary font-semibold" data-testid="consent-renewal-eyebrow">
                        {isFirstTime ? "One-time confirmation" : "Updated legal policies"}
                    </p>
                    <h2 className="mt-2 text-xl font-semibold" data-testid="consent-renewal-title">
                        {isFirstTime
                            ? "Please confirm you accept our policies"
                            : "Our policies have been updated"}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed" data-testid="consent-renewal-body">
                        {isFirstTime
                            ? "Before you continue, please confirm you have read and agree to the current versions of the following policies. This is a Kenya Data Protection Act requirement."
                            : "We have published new versions of the policies below. Under Kenya's Data Protection Act we cannot continue to serve your account until you review and re-accept them."}
                    </p>
                </div>

                <ScrollArea className="max-h-56 px-6 py-4">
                    <ul className="space-y-2">
                        {Object.entries(state.current).map(([key, meta]) => {
                            const info = POLICY_LABELS[key] || { label: key, href: "#" };
                            const changed = changedKeys.includes(key);
                            return (
                                <li
                                    key={key}
                                    className="flex items-center justify-between gap-3 text-sm"
                                    data-testid={`consent-policy-row-${key}`}
                                >
                                    <a
                                        href={info.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-foreground hover:text-primary underline underline-offset-2"
                                        data-testid={`consent-policy-link-${key}`}
                                    >
                                        {info.label}
                                    </a>
                                    <span className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground font-mono">
                                            v{meta.version}
                                        </span>
                                        {changed && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[10px] font-semibold uppercase tracking-widest">
                                                Updated
                                            </span>
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </ScrollArea>

                <div className="px-6 py-4 border-t border-border space-y-4">
                    <label className="flex items-start gap-3 text-sm text-foreground cursor-pointer">
                        <Checkbox
                            checked={confirmed}
                            onCheckedChange={setConfirmed}
                            data-testid="consent-renewal-checkbox"
                        />
                        <span>
                            I have read, understood, and agree to the policies listed above at the versions shown.
                        </span>
                    </label>

                    <div className="flex items-center justify-between gap-3">
                        <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => window.location.assign("/legal/terms")}
                            data-testid="consent-renewal-review-link"
                        >
                            Open the full policy pack →
                        </button>
                        <Button
                            disabled={!confirmed || submitting}
                            onClick={handleAccept}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                            data-testid="consent-renewal-accept-button"
                        >
                            {submitting ? "Recording…" : "I accept — continue"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
